import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';

import {
  useSupabaseClient,
  useSupabaseReady,
} from '../supabase/SupabaseProvider';
import type { Conversation, Message } from '../types';

const PAGE_SIZE = 50;

export interface ConversationListItem {
  conversation: Conversation;
  last_message: Message;
}

interface UseAdminConversationsResult {
  conversations: ConversationListItem[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
}

interface ConversationRow extends Conversation {
  messages: Message[] | null;
}

function toListItems(rows: ConversationRow[]): ConversationListItem[] {
  // Conversations without any messages yet are intentionally omitted —
  // the chat list only surfaces threads that have at least one exchange.
  return rows
    .filter((row) => row.messages && row.messages.length > 0)
    .map((row) => ({ conversation: row, last_message: row.messages![0] }));
}

export function useAdminConversations(): UseAdminConversationsResult {
  const client = useSupabaseClient();
  const ready = useSupabaseReady();
  const [conversations, setConversations] = useState<ConversationListItem[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const offsetRef = useRef(0);

  const fetchRange = useCallback(
    async (from: number, to: number): Promise<ConversationListItem[]> => {
      if (!client) return [];
      // !inner + neq message_type=broadcast means a conversation that
      // only ever received broadcast fanouts (no direct exchange yet)
      // is filtered out entirely — broadcasts live in the announcements
      // surface, not the chat list. The non-null last_message_at check
      // is belt-and-braces against pre-trigger-fix rows where a
      // broadcast had bumped last_message_at.
      const { data, error: err } = await client
        .from('conversations')
        .select(
          '*, messages!inner(id, conversation_id, sender_id, content, message_type, metadata, channel, created_at)',
        )
        .eq('type', 'worker_company')
        .not('last_message_at', 'is', null)
        .neq('messages.message_type', 'broadcast')
        .order('last_message_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false })
        .order('created_at', { foreignTable: 'messages', ascending: false })
        .limit(1, { foreignTable: 'messages' })
        .range(from, to);
      if (err) throw err;
      return toListItems((data as ConversationRow[]) ?? []);
    },
    [client],
  );

  const refresh = useCallback(async () => {
    if (!client || !ready) return;
    setLoading(true);
    setError(null);
    try {
      const items = await fetchRange(0, PAGE_SIZE - 1);
      setConversations(items);
      offsetRef.current = items.length;
      setHasMore(items.length === PAGE_SIZE);
    } catch (err: unknown) {
      Sentry.captureException(err, {
        tags: { feature: 'comms', action: 'fetch_admin_conversations' },
      });
      setError((err as Error)?.message ?? 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [client, ready, fetchRange]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore || loading) return;
    setLoadingMore(true);
    try {
      const from = offsetRef.current;
      const items = await fetchRange(from, from + PAGE_SIZE - 1);
      setConversations((prev) => [...prev, ...items]);
      offsetRef.current += items.length;
      setHasMore(items.length === PAGE_SIZE);
    } catch (err: unknown) {
      Sentry.captureException(err, {
        tags: { feature: 'comms', action: 'fetch_admin_conversations_more' },
      });
      setError((err as Error)?.message ?? 'Failed to load more conversations');
    } finally {
      setLoadingMore(false);
    }
  }, [fetchRange, hasMore, loading, loadingMore]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Realtime: subscribe to conversation row updates (last_message_at is
  // bumped by the messages-insert trigger) so the list reorders without
  // a hard refetch. Debounced so bursts of incoming messages coalesce
  // into a single refresh instead of N concurrent queries.
  useEffect(() => {
    if (!client || !ready) return;
    const channelName = `admin-conversations-feed:${crypto.randomUUID()}`;
    let pending: ReturnType<typeof setTimeout> | null = null;
    const scheduleRefresh = () => {
      if (pending) return;
      pending = setTimeout(() => {
        pending = null;
        void refresh();
      }, 250);
    };
    const channel = client.channel(channelName).on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'conversations',
        filter: 'type=eq.worker_company',
      },
      () => {
        scheduleRefresh();
      },
    );
    channel.subscribe();
    return () => {
      if (pending) clearTimeout(pending);
      void client.removeChannel(channel);
    };
  }, [client, ready, refresh]);

  return {
    conversations,
    loading,
    loadingMore,
    error,
    hasMore,
    refresh,
    loadMore,
  };
}
